/**
 * HotelsManager — Thin orchestrator for hotel UI.
 *
 * Mirrors EcommerceManager: owns booking state and delegates all UI
 * operations to the active presenter (HotelsDesktopPresenter or
 * HotelsMobilePresenter). The presenter is resolved lazily on first use.
 */
import { HotelsDesktopPresenter } from './HotelsDesktopPresenter.js';
import { HotelsMobilePresenter } from './HotelsMobilePresenter.js';

export class HotelsManager {
  constructor(config = {}) {
    this.shadowRoot = config.shadowRoot;
    this.primaryColor = config.primaryColor || '#2563eb';
    this.onRoomSelected = config.onRoomSelected || (() => {});
    this.onExtraSelected = config.onExtraSelected || (() => {});

    this._presenter = null;
  }

  get presenter() {
    if (!this._presenter) {
      this._presenter = this._createPresenter();
    }
    return this._presenter;
  }

  _createPresenter() {
    if (this._isMobileCall()) {
      return new HotelsMobilePresenter({
        primaryColor: this.primaryColor,
        onRoomBook: (room) => this._handleRoomBook(room),
      });
    }

    return new HotelsDesktopPresenter({
      shadowRoot: this.shadowRoot,
      primaryColor: this.primaryColor,
      onRoomBook: (room) => this._handleRoomBook(room),
    });
  }

  _isMobileCall() {
    return !!document.getElementById('mobile-voice-call-bar-container');
  }

  handleShowItems(message) {
    const items = message.items || message.products || [];
    if (!items.length) {
      console.warn('[HotelsManager] show_items with no items');
      return;
    }

    const rooms = items.map(mapRoom);
    this.presenter.showRooms(rooms, message.title);
  }

  handleCartUpdated(message) {
    const { product, action, cartTotal, cartItemCount } = message;

    if (cartTotal !== undefined && cartItemCount !== undefined) {
      this.presenter.updateBooking({
        roomName: product?.name || product?.productName || '',
        totalPrice: cartTotal,
        nights: cartItemCount,
        currency: message.currency,
      });
    }
  }

  handleBookingUpdated(message) {
    const booking = message.booking || message;
    this.presenter.updateBooking(booking);
  }

  handleShowExtras(message) {
    const extras = message.items || message.extras || [];
    if (!extras.length) return;

    const mapped = extras.map(mapExtra);
    this.presenter.showRooms(mapped, message.title || 'Extras');
  }

  hideRooms() {
    this.presenter.hideRooms();
  }

  _handleRoomBook(room) {
    this.onRoomSelected(room);
  }

  destroy() {
    if (this._presenter) {
      this._presenter.destroy();
      this._presenter = null;
    }
  }
}

function mapRoom(raw) {
  return {
    id: raw.roomId || raw.id,
    name: raw.roomName || raw.name,
    imageUrl: raw.imageUrl || raw.image_url || '',
    subtitle: buildSubtitle(raw),
    pricePerNight: raw.pricePerNight || raw.price_per_night || raw.price || 0,
    currency: raw.currency || 'USD',
    available: raw.available !== undefined ? raw.available : true,
    nights: 1,
  };
}

function mapExtra(raw) {
  return {
    id: raw.extraId || raw.id,
    name: raw.name,
    imageUrl: raw.imageUrl || raw.image_url || '',
    subtitle: raw.description || '',
    pricePerNight: raw.price || raw.pricePerNight || 0,
    currency: raw.currency || 'USD',
    available: true,
    nights: 1,
  };
}

function buildSubtitle(room) {
  const parts = [];
  if (room.beds) parts.push(`${room.beds} bed${room.beds > 1 ? 's' : ''}`);
  if (room.amenities?.length) parts.push(room.amenities.slice(0, 3).join(', '));
  return parts.join(' \u00B7 ');
}
